import { OfficeDocumentEditor } from "client/Api/Documents/common";
import {
	DocumentItemType,
	RecentDocumentItemViewModel
} from "client/Api/Documents/SharePoint/common";
import {
	ActionWithQuickActionIndex,
	ToolbarAction,
	ToolbarActionGroup
} from "client/Components";
import { openFileInWopi, openFileInSharePointViewer } from "client_legacy/Documents/SharePoint/documentOpener";
import { open as openInLocalApp } from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import { AlertManager } from "client/Hooks";
import {
	getItemsInPreferredOrder,
	UserEditorPreference
} from "client_legacy/Documents/officeDocumentsOpenPreference";
import { calculate, getOpenLocalizations } from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import {
	downloadDocument,
	downloadDocumentInline
} from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { openShareALinkDialog } from "../Dialogs/ShareLink/ShareALinkDialog";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { sendSharePointFileAsAttachment } from "client_legacy/Email/SendFile/sendFile";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import * as openLocationAction from "client_legacy/Documents/SharePoint/Actions/openLocationAction";
import { getDownloadFileUrlPath } from "client_legacy/Documents/SharePoint/UrlsProviders/documentsItemUrlsProviders";
import { openUrlFile } from "../../openUrlFile";

export type RecentDocumentActionContext = {
	selection: readonly RecentDocumentItemViewModel[];
	isSharingViaLinkEnabled: boolean;
	isMobile: boolean;
	isInSelectMode: boolean;
	shouldOpenInNewWindow?: boolean;
};

export type RecentDocumentAction = ActionWithQuickActionIndex<RecentDocumentActionContext>;
export type RecentDocumentActionAndOthers = ToolbarAction<RecentDocumentActionContext>;

const _openGroup: ToolbarActionGroup = {
	kind: "alternative",
	name: "open-group",
	icon: "folder-open",
	title: localize("_Open")
};

export type RecentDocumentActionHandlers = {
	openImageDialog: (item: RecentDocumentItemViewModel) => void;
	openLocation?: (item: RecentDocumentItemViewModel) => void;
};

export type RecentDocumentSelectionActionOptions = {
	alertManager: AlertManager;
	editorPreference: UserEditorPreference | undefined;
	handlers: RecentDocumentActionHandlers;
};

export function getRecentDocumentSelectionActions(
	options: RecentDocumentSelectionActionOptions
): readonly RecentDocumentAction[] {
	const {
		alertManager
	} = options;

	return [
		...getOpenActions(options),
		{
			kind: ActionKind.Regular,
			name: "OpenLocation",
			title: localize("_OpenLocation"),
			icon: "folder-open",
			canExecute: (context) => context.selection.length === 1,
			execute: (context) => {
				const item: RecentDocumentItemViewModel = context.selection[0];
				openLocationAction
					.execute(
						item,
						context.shouldOpenInNewWindow
					)
					.catch(
						() => alertManager.showError(localize("_GeneralErrorMessage"))
					);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "shareFile",
			title: localize("_ShareLink"),
			canExecute: (context) => {
				const {
					selection,
					isSharingViaLinkEnabled
				} = context;

				return isSharingViaLinkEnabled
					&& selection.length === 1;
			},
			execute: (context) => {
				openShareALinkDialog({
					document: context.selection[0]
				});
			},
			icon: "link",
			quickActionIndex: 0
		},
		{
			kind: ActionKind.Regular,
			name: "download",
			title: localize("_Download"),
			canExecute: (context) => {
				const {
					selection
				} = context;

				return selection.length === 1
					&& selection[0].itemType === DocumentItemType.File;
			},
			execute: (context) => {
				downloadDocument(context.selection[0]);
			},
			icon: "download"
		},
		{
			kind: ActionKind.Regular,
			name: "attachToEmail",
			title: localize("_AttachToEmail"),
			canExecute: (context) => {
				const { selection } = context;

				const emailClient: EmailClient = getEffectiveEmailClient();
				return emailClient.supportsEditDraft
					&& selection.length === 1
					&& selection[0].itemType === DocumentItemType.File; // OneNote is not supported
			},
			execute: (context) => {
				const emailClient: EmailClient = getEffectiveEmailClient();
				if (!emailClient.supportsEditDraft) {
					throw new Error("Current email app launcher does not support edit drafts.");
				}

				const document: RecentDocumentItemViewModel = context.selection[0];
				sendSharePointFileAsAttachment(
					document.id,
					convertToLocationModel(document.location),
					emailClient
				);
			},
			icon: "envelope"
		}
	];
}

export function getOpenActions(
	options: RecentDocumentSelectionActionOptions
): readonly RecentDocumentAction[] {
	const {
		alertManager,
		editorPreference,
		handlers: handler
	} = options;

	return [
		...getOpenOfficeDocActions(
			editorPreference
		),
		{
			kind: ActionKind.Regular,
			name: "OpenInOnlineViewer",
			group: _openGroup,
			title: localize("_OpenInAppName")
				.replace(
					"{0}",
					localize("_OnlineViewer")
				),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;

				if (selection.length !== 1) {
					return false;
				}

				const item: RecentDocumentItemViewModel = selection[0];
				return calculate(item)
					.canBeOpenedInSharePointViewer;
			},
			execute: (context) => {
				const item: RecentDocumentItemViewModel = context.selection[0];
					openFileInSharePointViewer(item);
			},
		},
		{
			kind: ActionKind.Regular,
			name: "OpenInBrowser",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				const item: RecentDocumentItemViewModel = selection[0];
				return calculate(item)
					.canBeOpenedInTheBrowser;
			},
			execute: (context) => {
				alertManager.showInfo(localize("_CannotOpenThisFileYet"));
				downloadDocumentInline(context.selection[0]);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "OpenInImagePreviewDialog",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;

				if (selection.length !== 1) {
					return false;
				}

				const item: RecentDocumentItemViewModel = selection[0];

				return calculate(item)
					.canBeOpenedInImagePreviewDialog;
			},
			execute: (context) => {
				handler.openImageDialog(context.selection[0]);
			}
		},
		{
			kind: ActionKind.Regular,
			name: "open-url-file",
			group: _openGroup,
			title: localize("_Open"),
			icon: "folder-open",
			canExecute: (context) => {
				const { selection } = context;
				if (selection.length !== 1) {
					return false;
				}

				const item: RecentDocumentItemViewModel = selection[0];

				return calculate(item).canBeOpenedAsExternalUrl;
			},
			execute: async(context) => {
				const item: RecentDocumentItemViewModel = context.selection[0];

				const url: string = getDownloadFileUrlPath(item.id, item.location);
				await openUrlFile(url, alertManager);
			}
		}
	];
}

function getOpenOfficeDocActions(
	editorPreference: UserEditorPreference | undefined
): readonly RecentDocumentAction[] {
	const { sharePoint } = documentsConfiguration();

	return getItemsInPreferredOrder<RecentDocumentAction>(
		sharePoint.defaultOfficeDocumentEditor,
		[
			[
				OfficeDocumentEditor.Online,
				() => ({
					kind: ActionKind.Regular,
					name: "OpenInOnlineEditor",
					group: _openGroup,
					title: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return "";
						}

						const item: RecentDocumentItemViewModel = selection[0];
						return getOpenLocalizations(item)
							.online
							.action;
					},
					icon: "folder-open",
					canExecute: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return false;
						}

						const item: RecentDocumentItemViewModel = selection[0];
						return calculate(item).canBeOpenedOnline;
					},
					execute: (c) => {
						const item: RecentDocumentItemViewModel = c.selection[0];
						openFileInWopi(item);
					}
				})
			],
			[
				OfficeDocumentEditor.Local,
				() => ({
					kind: ActionKind.Regular,
					name: "OpenInLocalApp",
					group: _openGroup,
					title: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return "";
						}

						const item: RecentDocumentItemViewModel = selection[0];
						return getOpenLocalizations(item)
							.local
							.action;
					},
					icon: "folder-open",
					canExecute: (c) => {
						const { selection } = c;
						if (selection.length !== 1) {
							return false;
						}

						const item: RecentDocumentItemViewModel = selection[0];
						return calculate(item).canBeOpenedLocally;
					},
					execute: (c) => {
						const item: RecentDocumentItemViewModel = c.selection[0];

						openInLocalApp(item);

						if (calculate(item).canBeOpenedOnline) {
							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								getOpenLocalizations(item),
								() => openFileInWopi(item)
							);
						}
					}
				})
			]
		],
		editorPreference
	);
}